import { Action, Dispatch } from "redux";
import { EnhancedStore } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { AreaOfInterest, CancelablePromise, Language, User, UserCreate, UserFollowService } from "../../client/api";
import client from "../../client";
import { UserActions, UserActionTypes } from "./types";
import { defaultGet, fetchError } from "./all";
import { checkMail, emailOrTel, errorLogging } from "../../config/utils";
import { normalizePhoneNumber } from "../../config/phone";
import { AS_AOI, AS_LANG } from "../../config/constants";
import { trackAmplitude } from "../../config/amplitude";

export function fetchMe(dispatch: Dispatch<Action<UserActionTypes>>): Action {
  if (client().loggedIn()) {
    client()
      .me()
      .then(
        (response) => {
          client().setLang(response.language.locale);
          dispatch({
            type: UserActionTypes.FETCH_SUCCESS,
            payload: response
          });
          UserFollowService.getUserFollowAllUserFollowV2AllGet()
            .then(
              (followData) => {
                dispatch({
                  type: UserActionTypes.ADD_FAVORITE_SUCCESS,
                  payload: followData
                });
              },
              (error) => {
                errorLogging(error, Sentry.Severity.Warning);
              }
            );
        },
        () => {
          dispatch({
            type: UserActionTypes.UNAUTHORIZED
          });
        }
      );
    return dispatch({ type: UserActionTypes.FETCH_ME });
  }
  return dispatch({
    type: UserActionTypes.FETCH_ERROR,
    payload: "not_registered"
  });
}

export function register(
  dispatch: Dispatch<Action<UserActionTypes>>,
  user: UserCreate
) {
  defaultGet(
    dispatch,
    UserActionTypes.FETCH_SUCCESS,
    UserActionTypes.FETCH_ERROR,
    UserActionTypes.REGISTER,
    client().register,
    false,
    false,
    user
  );
}

export function refreshToken(dispatch: Dispatch<Action<UserActionTypes>>) {
  dispatch({ type: UserActionTypes.REFRESH });
  client()
    .refreshToken()
    .then(
      (response) => {
        dispatch({
          type: UserActionTypes.FETCH_SUCCESS,
          payload: response
        });
        UserFollowService.getUserFollowAllUserFollowV2AllGet()
          .then(
            (followData) => {
              dispatch({
                type: UserActionTypes.ADD_FAVORITE_SUCCESS,
                payload: followData
              });
            },
            (error) => {
              errorLogging(error, Sentry.Severity.Warning);
            }
          );
      },
      () => {
        dispatch({
          type: UserActionTypes.FETCH_ERROR,
          payload: "not_registered"
        });
      }
    );
}

export function initBaseData(
  dispatch: Dispatch<Action<UserActionTypes>>,
  store: EnhancedStore
) {
  dispatch({ type: UserActionTypes.GET_BASE_DATA });
  const aoi = localStorage.getItem(AS_AOI);
  const language = localStorage.getItem(AS_LANG);
  if (aoi && language) {
    client()
      .getAoi(parseInt(aoi, 10), store)
      .then(
        (aoiObject) => {
          client()
            .getLanguage(parseInt(language, 10))
            .then(
              (languageObject) => {
                try {
                  dispatch({
                    type: UserActionTypes.BASE_DATA_SUCCESS,
                    payload: {
                      areaOfInterest: aoiObject,
                      language: languageObject
                    }
                  });
                } catch (e) {
                  dispatch({
                    type: UserActionTypes.BASE_DATA_SUCCESS,
                    payload: {
                      areaOfInterest: aoiObject,
                      favorites: [],
                      language: languageObject
                    }
                  });
                }
              },
              (error) => {
                fetchError(error, UserActionTypes.FETCH_ERROR, dispatch);
              }
            );
        },
        (error) => {
          fetchError(error, UserActionTypes.FETCH_ERROR, dispatch);
        }
      );
  } else {
    errorLogging({ aoi, language }, Sentry.Severity.Warning);
    dispatch({
      type: UserActionTypes.BASE_DATA_MISSING
    });
  }
}

export function changeLanguage(
  dispatch: Dispatch<Action<UserActionTypes>>,
  language: Language
) {
  localStorage.setItem(AS_LANG, language.id.toString(10));
  client().setLang(language.locale);
  defaultGet(
    dispatch,
    UserActionTypes.PATCH_SUCCESS,
    UserActionTypes.PATCH_FAILED,
    UserActionTypes.PATCH_USER,
    client().changeLanguage,
    false,
    false,
    language.id
  );
}

export function changeAoi(
  dispatch: Dispatch<Action<UserActionTypes>>,
  aoi: AreaOfInterest
) {
  localStorage.setItem(AS_AOI, aoi.id.toString(10));
  defaultGet(
    dispatch,
    UserActionTypes.PATCH_SUCCESS,
    UserActionTypes.PATCH_FAILED,
    UserActionTypes.PATCH_USER,
    client().changeAoi,
    false,
    false,
    aoi.id
  );
}

export function registerPatch(
  dispatch: Dispatch<UserActions>,
  username: string,
  givenName: string,
  familyName: string,
  email: string,
  password: string,
  passwordRepeat: string,
  tel: string,
  newsletter: boolean,
  reCaptchaToken: string
) {
  if (password !== passwordRepeat) {
    dispatch({
      type: UserActionTypes.REGISTER_FORM_INVALID,
      payload: "pwNotSame"
    });
    return;
  }
  if (password.length < 8) {
    dispatch({
      type: UserActionTypes.REGISTER_FORM_INVALID,
      payload: "pwNotValid"
    });
    return;
  }
  if (checkMail(email)) {
    client()
      .emailAvailable(email)
      .then(
        (response) => {
          if (response) {
            const normalizedTel = tel.trim() ? normalizePhoneNumber(tel) : tel;
            client()
              .telAvailable(normalizedTel)
              .then((responseTel) => {
                if (responseTel) {
                  defaultGet(
                    dispatch,
                    UserActionTypes.PATCH_SUCCESS,
                    UserActionTypes.PATCH_FAILED,
                    UserActionTypes.PATCH_USER,
                    client().registerPatch,
                    false,
                    false,
                    username,
                    givenName,
                    familyName,
                    email,
                    password,
                    normalizedTel,
                    newsletter,
                    reCaptchaToken
                  );
                } else {
                  dispatch({
                    type: UserActionTypes.REGISTER_FORM_INVALID,
                    payload: "telGiven"
                  });
                }
              });
          } else {
            dispatch({
              type: UserActionTypes.REGISTER_FORM_INVALID,
              payload: "mailGiven"
            });
          }
        },
        () => {
          dispatch({
            type: UserActionTypes.REGISTER_FORM_INVALID,
            payload: "mailGiven"
          });
        }
      );
  } else {
    dispatch({
      type: UserActionTypes.REGISTER_FORM_INVALID,
      payload: "mailInvalid"
    });
  }
}

export function login(
  dispatch: Dispatch<UserActions>,
  identifier: string,
  password: string
) {
  const normalized = identifier.trim();

  const performLogin = (
    promise: CancelablePromise<User>,
    wrongPayload: "pwWrong" | "telWrong"
  ) => {
    dispatch({ type: UserActionTypes.LOGIN });
    promise.then(
      (response) => {
        dispatch({
          type: UserActionTypes.PATCH_SUCCESS,
          payload: response
        });
        UserFollowService.getUserFollowAllUserFollowV2AllGet()
          .then(
            (followData) => {
              dispatch({
                type: UserActionTypes.ADD_FAVORITE_SUCCESS,
                payload: followData
              });
            },
            (error) => {
              errorLogging(error, Sentry.Severity.Warning);
            }
          );
      },
      (error) => {
        fetchError(error, UserActionTypes.PATCH_FAILED, dispatch);
        dispatch({
          type: UserActionTypes.LOGIN_FORM_INVALID,
          payload: wrongPayload
        });
      }
    );
  };

  if (checkMail(normalized)) {
    performLogin(client().authorize(normalized, password), "pwWrong");
    return;
  }

  const identifierType = emailOrTel(normalized);

  if (identifierType === "tel") {
    if (!normalized) {
      dispatch({
        type: UserActionTypes.LOGIN_FORM_INVALID,
        payload: "telInvalid"
      });
      return;
    }
    const normalizedTel = normalizePhoneNumber(normalized);
    performLogin(client().authorizeTel(normalizedTel, password), "telWrong");
    return;
  }

  dispatch({
    type: UserActionTypes.LOGIN_FORM_INVALID,
    payload: "mailInvalid"
  });
}

export function confirmTel(dispatch: Dispatch<UserActions>, code: string) {
  defaultGet(
    dispatch,
    UserActionTypes.CONFIRM_TEL_SUCCESS,
    UserActionTypes.CONFIRM_TEL_FAILED,
    UserActionTypes.PATCH_USER,
    client().confirmTel,
    false,
    false,
    code
  );
}

export function logout(dispatch: Dispatch<UserActions>) {
  client()
    .logout()
    .then(() => {
      dispatch({ type: UserActionTypes.LOGOUT });
      try {
        const target = "/home";
        if (window.location.pathname === target) {
          window.location.reload();
        } else {
          window.location.replace(target);
        }
      } catch (_) {
        window.location.href = "/home";
      }
    });
}

export function deleteAccount(dispatch: Dispatch<UserActions>) {
  defaultGet(
    dispatch,
    UserActionTypes.DELETE_ME_SUCCESS,
    UserActionTypes.FETCH_ERROR,
    UserActionTypes.PATCH_USER,
    client().deleteAccount,
    false,
    false
  );
}

export function addFavorite(
  dispatch: Dispatch<UserActions>,
  favorite: { id: number; type: "team" | "league" | "match" }
) {

  trackAmplitude(`follow_${favorite.type}`, { id: favorite.id });
  dispatch({ type: UserActionTypes.ADD_FAVORITE });
  client()
    .addFavorite(favorite)
    .then(
      (response) => {
        dispatch({
          type: UserActionTypes.ADD_FAVORITE_SUCCESS,
          payload: response
        });
      },
      (error) => {
        errorLogging(error, Sentry.Severity.Error);
        dispatch({
          type: UserActionTypes.ADD_FAVORITE_FAILED,
          payload: "not_registered"
        });
      }
    );
}

export function removeFavorite(
  dispatch: Dispatch<UserActions>,
  favorite: { id: number; type: "team" | "league" | "match" }
) {

  trackAmplitude(`unfollow_${  favorite.type}`, { id: favorite.id });
  dispatch({ type: UserActionTypes.REMOVE_FAVORITE });
  client()
    .removeFavorite(favorite)
    .then(
      (response) => {
        dispatch({
          type: UserActionTypes.REMOVE_FAVORITE_SUCCESS,
          payload: response
        });
      },
      (error) => {
        errorLogging(error, Sentry.Severity.Error);
        dispatch({
          type: UserActionTypes.ADD_FAVORITE_FAILED,
          payload: "not_registered"
        });
      }
    );
}
